/** @typedef {import("../../../api/types").Pipeline} Pipeline */
import React from "react";
import {Link} from "react-router-dom";
import InfoTip from "../../../shared/components/InfoTip";
import {formatMoney, formatPercent} from "../../../shared/utils/format";
import "./PipelineCard.css";

// The stages, from the least sure money to the most; `hint` says which orders are in it.
/** @type {{key: "negotiation"|"confirmed"|"to_receive"|"received", label: string, hint: string}[]} */
const STAGES = [
    {
        key: "negotiation",
        label: "Em negociação",
        hint: "Ordens abertas e enviadas, que o cliente ainda não respondeu. É dinheiro incerto.",
    },
    {
        key: "confirmed",
        label: "Confirmadas",
        hint: "Ordens que o cliente aceitou e cujo serviço ainda não foi concluído.",
    },
    {
        key: "to_receive",
        label: "A receber",
        hint: "Ordens concluídas que ainda não foram pagas.",
    },
    {
        key: "received",
        label: "Recebido neste mês",
        hint: "Ordens pagas desde o dia 1º deste mês, pela data em que foram marcadas como pagas.",
    },
];

/**
 * Where the money is, by how sure it is: from the quotes still being
 * answered to what was already paid this month, with what the month should
 * bring and what the quotes in negotiation might.
 *
 * @param {{data: Pipeline|null, error?: boolean}} props `data` is null while loading
 */
const PipelineCard = ({data, error = false}) => {
    let body;
    if (error) {
        body = <p className="pipeline__note">Não foi possível carregar esta parte.</p>;
    } else if (!data) {
        body = <p className="pipeline__note">Carregando…</p>;
    } else {
        const {currency, negotiation, forecast, conversion, weighted_negotiation: weighted} = data;
        body = (
            <>
                <ol className="pipeline__stages">
                    {STAGES.map(({key, label, hint}) => {
                        const stage = data[key];
                        return (
                            <li className="pipeline__stage" key={key}>
                                <div className="pipeline__stage-label">
                                    {label}
                                    <InfoTip>{hint}</InfoTip>
                                </div>
                                <div className="pipeline__stage-value">{formatMoney(stage.revenue, currency)}</div>
                                <div className="pipeline__stage-detail">
                                    {stage.orders} {stage.orders === 1 ? "ordem" : "ordens"} · lucro{" "}
                                    {formatMoney(stage.profit, currency)}
                                </div>
                            </li>
                        );
                    })}
                </ol>

                <p className="pipeline__forecast">
                    Previsão do mês: <strong>{formatMoney(forecast.revenue, currency)}</strong> de receita, com lucro de{" "}
                    <strong>{formatMoney(forecast.profit, currency)}</strong>
                    <InfoTip>
                        O que já foi recebido neste mês mais o que está a receber e o que está confirmado. As ordens em
                        negociação não entram: ainda dependem do cliente.
                    </InfoTip>
                </p>

                {negotiation.orders > 0 &&
                    (weighted && conversion ? (
                        <p className="pipeline__aside">
                            Se as ordens em negociação fecharem como de costume (
                            {formatPercent(Number(conversion.rate))} dos orçamentos enviados nos últimos{" "}
                            {conversion.days} dias foram confirmados): cerca de{" "}
                            <strong>{formatMoney(weighted.revenue, currency)}</strong> de receita e{" "}
                            <strong>{formatMoney(weighted.profit, currency)}</strong> de lucro.
                        </p>
                    ) : (
                        <p className="pipeline__aside">
                            Ainda não há orçamentos decididos o bastante para estimar quanto do que está em negociação
                            vai fechar.
                        </p>
                    ))}

                {data.expiring_soon > 0 && (
                    <p className="pipeline__aside">
                        <Link to="/orders?status=SE">
                            {data.expiring_soon}{" "}
                            {data.expiring_soon === 1 ? "orçamento enviado vence" : "orçamentos enviados vencem"} nos
                            próximos 7 dias
                        </Link>
                    </p>
                )}
            </>
        );
    }

    return (
        <section className="pipeline" aria-labelledby="pipeline-title">
            <h5 id="pipeline-title" className="mb-3">
                Dinheiro a caminho
            </h5>
            {body}
        </section>
    );
};

export default PipelineCard;
